import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../api.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Card, Field, Loading, inputClass } from '../components/ui.js';
import { downloadCsv, toCsv } from '../lib/csv.js';
import { formatCurrency } from '../lib/status.js';

interface CarrierTotal {
  carrierId: string;
  carrierName: string | null;
  paidNet: number;
  paidGross: number;
  chargebacks: number;
  periodsCovered: number;
}

const TOLERANCE = 1; // dollars — within $1 counts as a match (rounding).

/**
 * Year-end 1099 reconciliation — does the carrier's 1099 match what they actually
 * paid you? We tally the commission each carrier paid over the tax (calendar) year;
 * you enter the 1099 box amount; we flag the variance. A mismatch is mis-reported
 * income worth a conversation with the carrier (and your accountant).
 */
export function TaxReconciliation() {
  const { baseUrl, token } = useAuth();
  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];
  const [year, setYear] = useState(thisYear - 1); // last completed tax year
  const [workspaceId, setWorkspaceId] = useState('');
  const [carriers, setCarriers] = useState<CarrierTotal[] | null>(null);
  const [entered, setEntered] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setCarriers(null);
    const qs = new URLSearchParams({ year: String(year) });
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/tax-1099?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b) => active && setCarriers(b.carriers))
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [baseUrl, token, year, workspaceId]);

  const totals = useMemo(() => {
    let tallied = 0;
    let reported = 0;
    let hasEntries = false;
    for (const c of carriers ?? []) {
      tallied += c.paidNet;
      const e = Number(entered[c.carrierId]);
      if (entered[c.carrierId] && Number.isFinite(e)) {
        reported += e;
        hasEntries = true;
      }
    }
    return { tallied, reported, variance: reported - tallied, hasEntries };
  }, [carriers, entered]);

  const exportCsv = () => {
    if (!carriers) return;
    const rows = carriers.map((c) => {
      const e = Number(entered[c.carrierId]);
      const reported = entered[c.carrierId] && Number.isFinite(e) ? e : null;
      return {
        carrier: c.carrierName ?? c.carrierId,
        tallied: c.paidNet,
        reported1099: reported ?? '',
        variance: reported == null ? '' : Math.round((reported - c.paidNet) * 100) / 100,
      };
    });
    downloadCsv(
      `1099-reconciliation-${year}.csv`,
      toCsv(rows, [
        { key: 'carrier', header: 'Carrier' },
        { key: 'tallied', header: 'We tallied' },
        { key: 'reported1099', header: '1099 reported' },
        { key: 'variance', header: 'Variance' },
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">1099 reconciliation</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">
            We tally what each carrier actually paid you over the tax year (net of chargebacks).
            Enter the amount on their 1099 and we flag any mismatch — mis-reported income to take up
            with the carrier and your accountant.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="w-32">
            <Field label="Tax year">
              <select
                className={inputClass}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {loading && !carriers ? (
        <Loading label="Tallying the year…" />
      ) : !carriers || carriers.length === 0 ? (
        <Card>
          <p className="py-8 text-center text-sm text-neutral-500">
            No commission recorded for {year}. Pick a different tax year.
          </p>
        </Card>
      ) : (
        <>
          {totals.hasEntries && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat label="We tallied" value={formatCurrency(totals.tallied)} tone="neutral" />
              <Stat label="1099s reported" value={formatCurrency(totals.reported)} tone="neutral" />
              <Stat
                label="Variance (1099 − tallied)"
                value={`${totals.variance >= 0 ? '+' : ''}${formatCurrency(totals.variance)}`}
                tone={Math.abs(totals.variance) > TOLERANCE ? 'rose' : 'emerald'}
              />
            </div>
          )}

          <Card className="p-0">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
              <span className="text-sm font-semibold">Carriers · {year}</span>
              <button
                type="button"
                onClick={exportCsv}
                className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
              >
                Export CSV
              </button>
            </div>
            <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-3 px-5 py-2 text-xs font-medium text-neutral-500">
              <span>Carrier</span>
              <span className="text-right">We tallied (net)</span>
              <span className="text-right">1099 reported</span>
              <span className="text-right">Variance</span>
            </div>
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {carriers.map((c) => {
                const raw = entered[c.carrierId];
                const e = Number(raw);
                const reported = raw && Number.isFinite(e) ? e : null;
                const variance =
                  reported == null ? null : Math.round((reported - c.paidNet) * 100) / 100;
                const off = variance != null && Math.abs(variance) > TOLERANCE;
                return (
                  <div
                    key={c.carrierId}
                    className="grid grid-cols-[1.6fr_1fr_1fr_1fr] items-center gap-3 px-5 py-2.5 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {c.carrierName ?? c.carrierId.slice(0, 8)}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {c.periodsCovered} statement{c.periodsCovered === 1 ? '' : 's'}
                        {c.chargebacks > 0 ? ` · ${formatCurrency(c.chargebacks)} clawed back` : ''}
                      </div>
                    </div>
                    <span className="text-right font-medium tabular-nums">
                      {formatCurrency(c.paidNet)}
                    </span>
                    <span className="text-right">
                      <input
                        type="number"
                        step="any"
                        inputMode="decimal"
                        placeholder="—"
                        value={raw ?? ''}
                        onChange={(ev) =>
                          setEntered((s) => ({ ...s, [c.carrierId]: ev.target.value }))
                        }
                        className={`${inputClass} w-28 text-right`}
                      />
                    </span>
                    <span
                      className={`text-right font-medium tabular-nums ${
                        variance == null
                          ? 'text-neutral-300'
                          : off
                            ? 'text-rose-600 dark:text-rose-400'
                            : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {variance == null
                        ? '—'
                        : off
                          ? `${variance >= 0 ? '+' : ''}${formatCurrency(variance)}`
                          : '✓ match'}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
          <p className="text-xs text-neutral-500">
            "We tallied" is the net commission across the statements you loaded for {year}. If you
            didn't load every month, it will read low — load the missing statements first for an
            apples-to-apples check.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'rose' | 'emerald' | 'neutral';
}) {
  const color =
    tone === 'rose'
      ? 'text-rose-600 dark:text-rose-400'
      : tone === 'emerald'
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-neutral-900 dark:text-neutral-100';
  return (
    <Card>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${color}`}>{value}</div>
    </Card>
  );
}
